import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove, rectSortingStrategy, SortableContext } from '@dnd-kit/sortable';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  Tooltip,
  useTheme,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { MicrophoneIcon } from '@room-match/shared-ui-components'; // Remove StyledDialog
import { isEqual } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useHistory, useParams } from 'react-router-dom';
import ProgressBar from 'shared/components/ProfileProgressBar/ProgressBar';
import { ROUTES } from 'shared/constants/ROUTES';
import { CustomTypography } from 'shared/elements/CustomTypography';
import StyledDialog from 'shared/elements/StyledDialog/StyledDialog'; // Use talent app level folder StyledDialog
import useWindowDimensions from 'shared/hooks/useWindowDimenstion';
import IMedia from 'shared/interfaces/IMedia';
import {
  IAgencyTalentPermissionPayload,
  IProfileMedia,
  IProfileMediaSetSelectPayload,
} from 'shared/interfaces/IProfile';
import { mediaService } from 'shared/services/mediaService';
import { profileService } from 'shared/services/profileService';
import { errorResponseToArray } from 'shared/utils/room-shared';
import { v4 as uuidv4 } from 'uuid';
import {
  AddButton,
  CompletionCont,
  Content,
  Count,
  Fetching,
  Header,
  HeaderTitle,
  Hidden,
  HiddenCont,
  HLeft,
  HRight,
  Name,
  Percent,
  SectionCont,
} from './image-video-audio.styles';
import AudioItem from './Items/AudioItem';
import UploadingItem from './Items/UploadingItem';
import AddModal from './Modals/AddModal';

const { getMediaProfile, setSelectProfileMedia, unSelectProfileMedia, updateProfileMediaSort } = profileService();
const { getMediaList } = mediaService();
interface Props {
  fetching: boolean;
  handleAgencyTalentPermissions: (payload: IAgencyTalentPermissionPayload) => void;
  permissions?: string[];
  nonRepre: boolean;
}

interface IOutputE {
  file: File;
  id: string;
}

function Audios({ fetching, handleAgencyTalentPermissions, permissions, nonRepre }: Props) {
  const theme = useTheme();
  const { profileId } = useParams() as { profileId: string };
  const { data: mediaProfileData, isLoading: isMediaProfileLoading } = getMediaProfile(profileId, {
    file_type: 'audio',
  });
  const { data: mediaData, isLoading: isMediaLoading } = getMediaList({ file_type: 'audio', media_bin: true });
  const [expanded, setExpanded] = useState(true);
  const [audio, setAudio] = useState<IProfileMedia[]>([]);
  const [addModal, setAddModal] = useState(false);
  const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor));
  const { mutate: updateSortMutate } = updateProfileMediaSort();
  const [activeId, setActiveId] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { mutate, mutateAsync } = setSelectProfileMedia();
  const { mutate: unselectMutate } = unSelectProfileMedia();
  const [toUpload, settoUpload] = useState<IOutputE[]>([]);
  const { width } = useWindowDimensions();
  const history = useHistory();

  useEffect(() => {
    if (mediaProfileData) {
      setAudio(mediaProfileData.data);
    }
  }, [mediaProfileData]);

  const filteredMedia = useMemo(() => {
    const items = mediaData && mediaProfileData ? mediaData.data : [];
    return items.filter(
      (item) => !mediaProfileData?.data.some((profileItem) => profileItem.attributes.medium_id === item.id),
    );
  }, [mediaData, mediaProfileData]);

  const onDragEnd = () => {
    setActiveId(null);
    if (mediaProfileData) {
      const oldItems = mediaProfileData.data;
      const isMediaChanged = !isEqual(oldItems, audio);
      if (isMediaChanged) {
        const updatedItems = audio.map((item, i) => {
          return {
            id: item.id,
            sort: i,
          };
        });
        setTimeout(() => {
          updateSortMutate({ profileId, sort: updatedItems });
        }, 500);

        queryClient.setQueriesData(['profile_media', profileId, { file_type: 'audio' }], { data: audio });
      }
    }
  };

  const handleUnselectMedia = (profileMediaId: string) => {
    unselectMutate(
      { profileId, profileMediaId },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(['profile_media', profileId, { file_type: 'audio' }]);
        },
        onError: (errors) => {
          if (errors?.response?.data?.errors) {
            const errorResponseArray: any = errorResponseToArray(errors.response.data.errors);
            console.error('error', errorResponseArray.join(','));
          } else {
            console.error('error', 'Something went wrong');
          }
        },
      },
    );
  };

  const handleSetSelectMedia = (payload: IProfileMediaSetSelectPayload) => {
    mutate(
      { profileId, payload: { ...payload, sort: audio ? audio.length : 0 } },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(['profile_media', profileId, { file_type: 'audio' }]);
        },
        onError: (errors) => {
          if (errors?.response?.data?.errors) {
            const errorResponseArray: any = errorResponseToArray(errors.response.data.errors);
            console.error('error', errorResponseArray.join(','));
          } else {
            console.error('error', 'Something went wrong');
          }
        },
      },
    );
  };

  const handleCheck = (a: any) => {
    if (audio.find((aud) => aud.id === a.id)) {
      handleUnselectMedia(a.id);
    } else {
      handleSetSelectMedia({
        id: a.id,
        tag_list: 'audio',
      });
    }
  };

  const onDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id);
  };

  const onDragOver = (event: DragEndEvent) => {
    handleAgencyTalentPermissions({
      module: 'audio_files',
      callback: () => {
        const { active, over } = event;
        if (active && over) {
          if (active?.id !== over?.id) {
            setAudio((items: IProfileMedia[]) => {
              const oldIndex = items.findIndex((x: IProfileMedia) => x?.id === active?.id);
              const newIndex = items.findIndex((x: IProfileMedia) => x?.id === over?.id);
              const movedItems = arrayMove(items, oldIndex, newIndex);
              return movedItems;
            });
          }
        }
      },
      ctaText: 'OK',
    });
  };

  const computedWidth = (width: number, height: number) => (width * 250) / height;

  const completion = () => {
    switch (audio.length) {
      case 0:
        return 0;
      default:
        return 100;
    }
  };

  const bindToProfile = async (payload: IProfileMediaSetSelectPayload, imgID: string) => {
    await mutateAsync(
      { profileId, payload: { ...payload, sort: mediaProfileData ? mediaProfileData.data.length : 0 } },
      {
        onError: (errors) => {
          if (errors?.response?.data?.errors) {
            const errorResponseArray: any = errorResponseToArray(errors.response.data.errors);
            console.error('error', errorResponseArray.join(','));
          } else {
            console.error('error', 'Something went wrong');
          }
        },
      },
    );
    queryClient.invalidateQueries(['profile_media', profileId, { file_type: 'audio' }]);
    queryClient.invalidateQueries(['media', { file_type: 'audio' }]);
    queryClient.invalidateQueries(['profile', profileId]);
    settoUpload((img) => img.filter((f) => f.id !== imgID));
  };

  const handleOutput = (aud: File[]) => {
    const mAud = aud.map((a, c) => ({ file: a, id: uuidv4() }));
    settoUpload(mAud);
  };

  return (
    <SectionCont>
      <Accordion
        expanded={expanded}
        onChange={() => setExpanded(!expanded)}
        style={{ backgroundColor: 'transparent', margin: 0 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1bh-content" id="panel1bh-header">
          <Header>
            <HLeft>
              {width < 960 ? (
                <MicrophoneIcon style={{ width: 30, height: 30, alignSelf: 'center' }} />
              ) : (
                <MicrophoneIcon style={{ width: 40, height: 40, alignSelf: 'center' }} />
              )}
              <HeaderTitle>
                <Name>Audio</Name>
                <Count>
                  {audio.length ? `${audio.length} of ${audio.length + filteredMedia.length} used` : 'none'}
                </Count>
              </HeaderTitle>
              {width < 960 && (
                <AddButton
                  onClick={(e) => {
                    history.push(`${ROUTES.TALENT.MEDIA}/audios`);
                  }}
                >
                  EDIT
                </AddButton>
              )}
              {width < 960 && (
                <AddButton
                  style={{ marginLeft: 10 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAgencyTalentPermissions({
                      module: 'audio_files',
                      callback: () => {
                        setAddModal(true);
                      },
                    });
                  }}
                >
                  ADD
                </AddButton>
              )}
            </HLeft>
            <HRight>
              <CompletionCont>
                <Percent
                  style={{ color: `${completion() === 100 ? '#1fa00f' : completion() > 50 ? '#daa13e' : '#e34343'}` }}
                >
                  {completion()}% Complete
                </Percent>
                <ProgressBar
                  borderRadius={8}
                  percent={completion()}
                  completeColor={`${completion() === 100 ? '#1fa00f' : completion() > 50 ? '#daa13e' : '#e34343'}`}
                  backgroundColor={theme.palette.type === 'dark' ? '#38485d' : 'lightgray'}
                />
              </CompletionCont>
              {width > 960 && (
                <AddButton
                  onClick={(e) => {
                    history.push(`${ROUTES.TALENT.MEDIA}/audios`);
                  }}
                >
                  EDIT
                </AddButton>
              )}
              {width > 960 && (
                <>
                  {!permissions?.includes('Audio Files') && permissions && !nonRepre ? (
                    <Tooltip
                      title="Your agency has disabled the ability to edit the media on this profile, use the media manager to upload your files so they are accessible to your agency to include on your profile."
                      placement="top"
                      PopperProps={{ style: { marginBottom: -10 } }}
                    >
                      <div style={{ opacity: 0.5 }}>
                        <AddButton
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                          disabled
                        >
                          ADD AUDIO
                        </AddButton>
                      </div>
                    </Tooltip>
                  ) : (
                    <>
                      <AddButton
                        onClick={(e) => {
                          e.stopPropagation();
                          setAddModal(true);
                        }}
                      >
                        ADD AUDIO
                      </AddButton>
                    </>
                  )}
                </>
              )}
            </HRight>
          </Header>
        </AccordionSummary>

        <AccordionDetails>
          <Content>
            {fetching ? (
              <Fetching>
                <CircularProgress
                  style={{
                    width: 20,
                    height: 20,
                    marginRight: 10,
                  }}
                />
                Fetching Data...
              </Fetching>
            ) : audio.length || toUpload.length ? (
              <>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragStart={onDragStart}
                  onDragOver={onDragOver}
                  onDragEnd={onDragEnd}
                  onDragCancel={onDragEnd}
                >
                  <SortableContext items={audio} strategy={rectSortingStrategy}>
                    <Box
                      style={{
                        display: 'flex',
                        width: '100%',
                        flexWrap: 'wrap',
                      }}
                    >
                      {mediaProfileData &&
                        audio.map((item, i) => (
                          <AudioItem
                            dragging={!!activeId}
                            key={item.id}
                            aud={item}
                            src={item.attributes.attachment}
                            idx={(i + 1).toString()}
                            checkReturn={handleCheck}
                            handleAgencyTalentPermissions={handleAgencyTalentPermissions}
                          />
                        ))}
                    </Box>
                    <DragOverlay>
                      {activeId ? (
                        <div>
                          {
                            <AudioItem
                              dragging
                              idx={(audio.map((a) => a.id).indexOf(activeId) + 1).toString()}
                              aud={audio.find((item: any) => item.id === activeId)}
                              src={audio.find((item: any) => item.id === activeId)?.attributes.attachment || ''}
                              handleAgencyTalentPermissions={handleAgencyTalentPermissions}
                            />
                          }
                        </div>
                      ) : null}
                    </DragOverlay>
                  </SortableContext>
                </DndContext>
              </>
            ) : (
              <Fetching>
                <CustomTypography fontSize={18}>No Audios Selected</CustomTypography>
                {filteredMedia.length ? (
                  <CustomTypography fontSize={15}>
                    You can select from audios below or upload a new one.
                  </CustomTypography>
                ) : (
                  <CustomTypography fontSize={15}>
                    You can upload new audio by clicking the 'ADD AUDIO' button.
                  </CustomTypography>
                )}
              </Fetching>
            )}
            {toUpload.map((data) => (
              <UploadingItem key={data.id} type="audio" data={data.file} bindToProfile={bindToProfile} id={data.id} />
            ))}
            {!!filteredMedia.length && (
              <HiddenCont>
                <Hidden>Hidden Audios</Hidden>
                <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                  {filteredMedia.map((aud: IMedia, idx: number) => (
                    <AudioItem
                      key={aud.id}
                      checked={false}
                      check
                      aud={aud}
                      src={aud.attributes.attachment_url}
                      idx={(idx + 1).toString()}
                      checkReturn={handleCheck}
                      handleAgencyTalentPermissions={handleAgencyTalentPermissions}
                    />
                  ))}
                </div>
              </HiddenCont>
            )}
          </Content>
        </AccordionDetails>
      </Accordion>
      <StyledDialog
        style={{ zIndex: 9999 }}
        open={addModal}
        onClose={() => setAddModal(false)}
        maxWidth="md"
        aria-labelledby="simple-dialog-title"
      >
        <AddModal close={() => setAddModal(false)} type="Audio" output={handleOutput} />
      </StyledDialog>
    </SectionCont>
  );
}

export default Audios;
